/**
 * Site-geofence monitor. While an event is open, watches GPS in the
 * background; if the tech leaves the site (outside polygon, or
 * outside the circular fallback) for ≥60s sustained AND there are
 * unsampled plan items, fires `onLeftWithUnsampled` once. The host
 * component decides what to do (typically: open a non-dismissible
 * bottom sheet offering "Return", "Save & continue later", or
 * "Close event with reason").
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGpsFix, haversineMeters } from './useGpsFix';

export interface SitePolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

interface MonitorOpts {
  enabled: boolean;
  /** Site centroid for the circular fallback. */
  centroid: { lat: number; lon: number } | null;
  /** GeoJSON polygon (preferred). When null, uses centroid + radius_m. */
  polygon?: SitePolygon | null;
  radius_m?: number | null;
  /** Sustained-outside time before firing in ms. Default 60s. */
  sustainedMs?: number;
  /** Number of plan items still pending. Detector only fires when >0. */
  pendingCount: number;
  /** Called once when threshold crossed. Until reset(), does not refire. */
  onLeftWithUnsampled?: (info: {
    distance_m: number;
    pendingCount: number;
  }) => void;
}

interface MonitorState {
  inside: boolean | null; // null = no fix yet
  distance_m: number | null;
  fired: boolean;
}

/** Fast point-in-polygon (ray casting). Adequate for the small,
 *  non-self-intersecting polygons sites typically have; doesn't
 *  warrant a full @turf dep. Coordinates in [lng, lat] order to match
 *  the GeoJSON convention. */
function pointInPolygon(
  pt: { lat: number; lon: number },
  polygon: SitePolygon,
): boolean {
  const ring = polygon.coordinates[0];
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > pt.lat !== yj > pt.lat &&
      pt.lon < ((xj - xi) * (pt.lat - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function useSiteGeofenceMonitor(opts: MonitorOpts): MonitorState & {
  reset(): void;
} {
  const gps = useGpsFix(opts.enabled);
  const [state, setState] = useState<MonitorState>({
    inside: null,
    distance_m: null,
    fired: false,
  });
  const outsideSinceRef = useRef<number | null>(null);
  const sustainedMs = opts.sustainedMs ?? 60_000;
  const fired = state.fired;

  // Memoize the polygon ring so identity stays stable when callers
  // pass a JSON deserialization that hasn't actually changed.
  const polygonKey = useMemo(
    () => (opts.polygon ? JSON.stringify(opts.polygon) : null),
    [opts.polygon],
  );

  useEffect(() => {
    if (!opts.enabled || !gps.fix) return;
    const fix = gps.fix;
    let inside: boolean;
    let distance: number;
    if (opts.polygon) {
      inside = pointInPolygon(fix, opts.polygon);
      // Approximate distance: 0 if inside, else from centroid (good
      // enough for the UI message; we never gate behavior on this).
      distance =
        opts.centroid != null
          ? haversineMeters(fix, opts.centroid)
          : 0;
    } else if (opts.centroid != null) {
      const r = opts.radius_m ?? 30;
      distance = haversineMeters(fix, opts.centroid);
      inside = distance <= r;
    } else {
      // Nothing to compare against — treat as inside (don't false-alarm
      // when a site has no geofence configured).
      inside = true;
      distance = 0;
    }

    setState((s) => ({ ...s, inside, distance_m: distance }));

    if (inside) {
      outsideSinceRef.current = null;
      return;
    }

    // Outside. Track sustained-outside duration.
    if (outsideSinceRef.current == null) {
      outsideSinceRef.current = Date.now();
    }
    const elapsed = Date.now() - outsideSinceRef.current;
    if (
      elapsed >= sustainedMs &&
      opts.pendingCount > 0 &&
      !fired &&
      opts.onLeftWithUnsampled
    ) {
      setState((s) => ({ ...s, fired: true }));
      opts.onLeftWithUnsampled({
        distance_m: distance,
        pendingCount: opts.pendingCount,
      });
    }
  }, [
    gps.fix,
    opts.enabled,
    polygonKey,
    opts.centroid?.lat,
    opts.centroid?.lon,
    opts.radius_m,
    opts.pendingCount,
    sustainedMs,
    fired,
    opts.onLeftWithUnsampled,
    opts.polygon,
    opts.centroid,
  ]);

  return {
    ...state,
    reset: () => {
      outsideSinceRef.current = null;
      setState((s) => ({ ...s, fired: false }));
    },
  };
}
